/**
 * Política do passe de humanidade — valores compartilhados por servidor e cliente.
 *
 * Fica fora de `humanPass.ts` de propósito: aquele módulo é SOMENTE SERVIDOR
 * (usa `node:crypto` e `next/headers`) e não pode ser importado de um client
 * component. O widget precisa saber quando o passe vence para renová-lo antes
 * da hora, e a única forma de os dois lados não divergirem é a constante viver
 * num arquivo sem dependência de runtime.
 */

/** Janela de validade do passe. Cobre o preenchimento do formulário com folga. */
export const HUMAN_PASS_TTL_MS = 10 * 60 * 1000;

/**
 * Consultas por desafio resolvido.
 *
 * Já foi 10, com a justificativa de que "usuário real digita um CPF e no
 * máximo corrige alguns dígitos". A produção desmentiu: nos primeiros minutos
 * com a exigência ligada foram 112 negativas por cota esgotada em 10 minutos,
 * todas de cliente real -- o atacante nem chegava na action. A consulta dispara
 * enquanto se digita, com debounce, então corrigir o CPF e navegar entre login
 * e checkout consome várias unidades sem que ninguém perceba.
 *
 * Subiu para 100 (#687) e desceu para 20 quando a cota foi para o servidor
 * (#713). Com a cota no servidor, o número é o que o atacante leva por desafio
 * resolvido: o robô de 22/09 esgotou dois passes em minutos assim que achou a
 * action nova. A medição da produção em 22/09, com 1.192 passes vencidos, deu
 * p99 de 2 consultas, p99,9 de 4 e máximo de 7 por passe. 91% nem foram usados,
 * porque o widget renova o passe a cada 8 minutos (#709) e cada passe cobre uma
 * janela curta. 20 deixa quase 3x de folga sobre o maior uso real e corta 5x o
 * rendimento de cada desafio para quem automatiza.
 *
 * Voltar para 10 não: fica colado no máximo observado, e estourar a cota ainda
 * custa ao cliente um desafio novo, mesmo que quase sempre invisível.
 */
export const HUMAN_PASS_QUOTA = 20;

/**
 * CPFs diferentes que um mesmo passe pode consultar.
 *
 * A cota limita quantas consultas cabem num desafio, mas não o que elas
 * revelam: 20 consultas podem ser 20 CPFs diferentes. Foi isso que o robô de
 * 23/09 passou a fazer. Espalhou as chamadas por um IP por requisição e usou
 * cada passe abaixo de 20 para não gerar negativa `exhausted`, que é o que o
 * monitor vigia. Cliente real consulta o próprio CPF; às vezes um segundo, por
 * erro de digitação ou para comprar para alguém da família. Com 3, quem
 * automatiza precisa de um desafio novo a cada 3 CPFs, não a cada 20. Repetir
 * o mesmo CPF não conta, porque a consulta dispara a cada tecla com debounce.
 *
 * Desceu para 1 em 24/09. Com 3, o robô passou a resolver o Turnstile em massa
 * (widget já em modo `managed`) e chegou a ~230 CPFs a cada 10 minutos pela
 * `getCustomerGuestCpf`, cada CPF fora da base virando consulta paga no
 * provedor. Com 1, cada CPF custa um desafio resolvido. Para o cliente real,
 * o segundo CPF da sessão -- erro de digitação, compra para outra pessoa --
 * custa um desafio a mais, quase sempre invisível: o `runWithPass` do
 * HumanPassProvider resolve outro desafio e repete a consulta sozinho.
 */
export const HUMAN_PASS_MAX_DISTINCT_CPFS = 1;

/**
 * Quando o cliente resolve um desafio novo, antes de o passe vencer.
 *
 * O fim de semana de 19-20/09 negou 1.813 consultas por `expired`: o passe vale
 * 10 minutos e nada o renovava, então toda sessão mais longa que isso -- que é
 * exatamente o caso de quem acompanha o sorteio com a aba aberta -- era negada
 * na consulta seguinte. A margem de 2 minutos cobre resolver o desafio e o
 * round-trip do siteverify sem deixar a sessão descoberta em nenhum instante.
 */
export const HUMAN_PASS_RENEW_AFTER_MS = HUMAN_PASS_TTL_MS - 2 * 60 * 1000;

/**
 * Teto para a consulta esperar o passe existir antes de tentar de todo jeito.
 *
 * A consulta de CPF dispara no debounce da digitação, e o passe nasce de uma
 * cadeia assíncrona (script do Turnstile, render, desafio, siteverify, cookie).
 * Quem digita rápido chegava na action antes de o cookie existir: é a corrida
 * que respondeu por 6.119 negativas `missing` no fim de semana de 19-20/09,
 * 71% do total, e que explodia justamente nos picos de audiência, quando entram
 * muitas sessões novas ao mesmo tempo e o Lambda está sob carga.
 *
 * O teto existe para a espera nunca virar tela travada: esgotado, a consulta
 * segue e, se for negada, cai no caminho de negativa que já existe.
 */
export const HUMAN_PASS_WAIT_TIMEOUT_MS = 8_000;
